import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import {
  useCompanies,
  useCreateCompany,
  useUpdateCompany,
  useLabs,
} from "@/hooks/api";
import type { Company } from "@/lib/api_client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import {
  type CompanyFormState,
  type FieldErrors,
  type RowSaveState,
  companyToForm,
  emptyForm,
  flowTypeLabel,
  formToPayload,
  initials,
  isDeactivated,
  isDirty,
  matchesSearch,
  rowStatus,
  validateForm,
} from "./company-form";
import {
  CompanyPanel,
  EmptyPanel,
  type PanelTab,
} from "./company-panel";

const NEW = "new";

export function CompaniesPage() {
  const { data: companies, isLoading } = useCompanies();
  const { data: labs } = useLabs();
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();

  // Rule 2: the selected record lives in the URL, so the link can be shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasRole } = useAuth();
  // Company writes are admin-only in the API; superuser alone does not grant them.
  const readOnly = !hasRole("admin");
  const selectedId = searchParams.get("id");
  const tab: PanelTab =
    searchParams.get("tab") === "innlogging" ? "innlogging" : "grunninfo";

  const [query, setQuery] = useState("");
  const [form, setForm] = useState<CompanyFormState>({ ...emptyForm });
  const [baseline, setBaseline] = useState<CompanyFormState>({ ...emptyForm });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<{
    id: string;
    state: RowSaveState;
  } | null>(null);
  // The list refetches after a create, so hold the new record until it lands.
  const [justCreated, setJustCreated] = useState<Company | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  const selectedCompany =
    selectedId && selectedId !== NEW
      ? ((companies ?? []).find((c) => c.id === selectedId) ??
        (justCreated?.id === selectedId ? justCreated : null))
      : null;

  // Load the selected record into the form. Selection only changes once the
  // dirty guard below has let it through — and it replaces the history entry
  // rather than pushing one, so Back cannot change it behind the guard either.
  if (
    companies &&
    selectedId !== loadedId &&
    (selectedId === null || selectedId === NEW || selectedCompany)
  ) {
    const next = selectedCompany
      ? companyToForm(selectedCompany)
      : { ...emptyForm };
    setLoadedId(selectedId);
    setForm(next);
    setBaseline(next);
    setErrors({});
  }

  const dirty = isDirty(form, baseline);
  const saving = createCompany.isPending || updateCompany.isPending;

  const applySelection = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("id", id);
    // Rule 3: Grunninfo is always the tab a record opens on.
    next.set("tab", "grunninfo");
    // Replace, never push: a pushed entry lets Back swap the record without
    // passing the dirty guard, which would drop the edits in silence.
    setSearchParams(next, { replace: true });
    setPendingId(null);
    setSaveState(null);
    setErrors({});
  };

  /** Rule 5: dirty state belongs to the record — switching away has to ask. */
  const select = (id: string) => {
    if (id === selectedId) return;
    if (dirty) {
      setPendingId(id);
      return;
    }
    applySelection(id);
  };

  const save = async (thenSelect?: string) => {
    const found = validateForm(form, labs ?? []);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setPendingId(null);
      return;
    }
    const payload = formToPayload(form);
    const rowId = selectedCompany?.id ?? NEW;
    setSaveState({ id: rowId, state: "saving" });
    try {
      if (selectedCompany) {
        await updateCompany.mutateAsync({
          id: selectedCompany.id,
          data: payload,
        });
        setBaseline(form);
        toast.success("Bedrift lagret");
        if (thenSelect !== undefined) applySelection(thenSelect);
        setSaveState({ id: selectedCompany.id, state: "saved" });
      } else {
        const created = await createCompany.mutateAsync(payload);
        setJustCreated(created);
        setBaseline(form);
        toast.success("Bedrift opprettet");
        applySelection(thenSelect !== undefined ? thenSelect : created.id);
        setSaveState({ id: created.id, state: "saved" });
      }
    } catch (e: any) {
      setSaveState({ id: rowId, state: "error" });
      setPendingId(null);
      toast.error(e.message ?? "Noe gikk galt");
    }
  };

  // Rule 4: ⌘S saves, and nothing else does.
  const panelOpen = selectedId === NEW || Boolean(selectedCompany);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        // The record can leave the list under us (deleted elsewhere); then the
        // panel is gone and saving the stale form would create a duplicate.
        if (panelOpen && dirty && !saving) void save();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const labName = (labId: string | null) =>
    labId ? ((labs ?? []).find((l) => l.id === labId)?.name ?? null) : null;

  const visible = (companies ?? []).filter((c) =>
    matchesSearch(c, query, labName(c.labId)),
  );
  const pendingCompany =
    pendingId && pendingId !== NEW
      ? ((companies ?? []).find((c) => c.id === pendingId) ?? null)
      : null;
  const pendingName = pendingId
    ? pendingId === NEW
      ? "Ny bedrift"
      : (pendingCompany?.name ?? "en annen bedrift")
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 overflow-hidden rounded-lg border border-admin-card-border bg-admin-bg">
      <div className="flex w-[302px] shrink-0 flex-col border-r border-admin-card-border">
        <div className="border-b border-admin-card-border px-4 py-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold">
              Bedrifter{" "}
              <span className="font-normal text-admin-text-subtle">
                {visible.length}
              </span>
            </h2>
            {!readOnly && (
              <Button variant="ghost" size="sm" onClick={() => select(NEW)}>
                <Plus className="h-3 w-3" />
                Ny
              </Button>
            )}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-admin-text-subtle" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søk etter bedrift, lab eller flyt"
              className="pl-7"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {/* Rule 1: a list is a table — rows, not cards. */}
          {visible.map((company) => {
            const selected = company.id === selectedId;
            const status = rowStatus(
              selected && dirty,
              saveState?.id === company.id ? saveState.state : undefined,
            );
            return (
              <div
                key={company.id}
                role="button"
                tabIndex={0}
                onClick={() => select(company.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    select(company.id);
                  }
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2.5 border-b border-l-2 border-admin-card-border px-3 py-2.5 text-left transition-colors hover:bg-admin-card-hover",
                  selected
                    ? "border-l-admin-primary bg-admin-card"
                    : "border-l-transparent",
                )}
              >
                {company.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt=""
                    className="h-7 w-7 shrink-0 rounded-md bg-white object-contain p-0.5"
                  />
                ) : (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-admin-card-header text-xs font-semibold text-admin-primary">
                    {initials(company.name)}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">
                    {company.name}
                  </span>
                  <span className="block truncate text-xs text-admin-text-subtle">
                    {flowTypeLabel(company.flowType)} ·{" "}
                    {company.incomePerEmployee
                      ? `${Number(company.incomePerEmployee).toLocaleString("nb-NO")} kr`
                      : "inntekt mangler"}
                  </span>
                </span>
                {/* Rule 6: the row is the status line. */}
                {status ? (
                  <span className="flex shrink-0 items-center gap-1.5">
                    <span
                      className={cn(
                        "text-[10px] font-medium",
                        status.tone === "error"
                          ? "text-admin-negative"
                          : status.tone === "saved"
                            ? "text-admin-success-400"
                            : status.tone === "dirty"
                              ? "text-admin-warn-400"
                              : "text-admin-text-subtle",
                      )}
                    >
                      {status.label}
                    </span>
                    {status.tone === "error" && selected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void save();
                        }}
                        className="cursor-pointer text-[10px] font-medium text-admin-info hover:underline"
                      >
                        Prøv igjen
                      </button>
                    )}
                  </span>
                ) : (
                  <RowState company={company} />
                )}
              </div>
            );
          })}
          {visible.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-admin-text-subtle">
              Ingen bedrifter matcher søket.
            </p>
          )}
        </div>

        <div className="border-t border-admin-card-border px-3 py-2 text-[11px] text-admin-text-subtle">
          ⌘S lagrer · Ny åpner en tom post
        </div>
      </div>

      <div className="min-w-0 flex-1 overflow-hidden">
        {/* A link to a company that no longer exists lands on the empty panel,
            never on a blank form that would create one. */}
        {selectedId === NEW || selectedCompany ? (
          <CompanyPanel
            company={selectedCompany}
            form={form}
            errors={errors}
            labs={labs ?? []}
            tab={tab}
            onTabChange={(next) => {
              const params = new URLSearchParams(searchParams);
              params.set("tab", next);
              setSearchParams(params, { replace: true });
            }}
            onChange={(field, value) =>
              setForm((f) => ({ ...f, [field]: value }))
            }
            dirty={dirty}
            saving={saving}
            onSave={() => void save()}
            onCancel={() => {
              setForm(baseline);
              setErrors({});
            }}
            pendingName={pendingName}
            onDiscardPending={() => pendingId && applySelection(pendingId)}
            onSavePending={() => pendingId && void save(pendingId)}
            readOnly={readOnly}
          />
        ) : (
          <EmptyPanel />
        )}
      </div>
    </div>
  );
}

/** The company's own state, shown when the row has no save status to report. */
function RowState({ company }: { company: Company }) {
  if (isDeactivated(company)) {
    return (
      <span className="shrink-0 text-[10px] font-medium text-admin-negative">
        Deaktivert
      </span>
    );
  }
  if (company.deactivatedAt) {
    return (
      <span className="shrink-0 text-[10px] font-medium text-admin-warn-400">
        {new Date(company.deactivatedAt).toLocaleDateString("nb-NO")}
      </span>
    );
  }
  return null;
}
